/**
 * Dead Letter Queue — eventos críticos nunca descartados após esgotar retries.
 */

import type { SendEventParams } from '../utils/sendEvent';
import type { QueueItemType } from './queue';

const DEAD_QUEUE_STORAGE_KEY = 'GOMARKE_DEAD_LETTER_V1';

const CRITICAL_EVENT_TYPES = new Set<string>([
  'PURCHASE',
  'LEAD',
  'INITIATE_CHECKOUT',
  'ADD_PAYMENT_INFO',
  'SUBSCRIPTION',
]);

export interface DeadLetterEntry {
  id: string;
  queueItemId: string;
  type: SendEventParams['type'];
  queueType: QueueItemType;
  params: SendEventParams;
  attempts: number;
  error?: string;
  reason: string;
  timestamp: number;
}

let memoryDeadLetter: DeadLetterEntry[] = [];

function isCriticalEvent(type: SendEventParams['type']): boolean {
  return CRITICAL_EVENT_TYPES.has(type);
}

function persist(entries: DeadLetterEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    const minimal = entries.map(e => ({
      ...e,
      params: {
        ...e.params,
        config: {
          apiEndpoint: e.params.config.apiEndpoint,
          pixel: e.params.config.pixel,
          debug: e.params.config.debug ?? false,
        },
      },
    }));
    window.localStorage.setItem(DEAD_QUEUE_STORAGE_KEY, JSON.stringify(minimal));
  } catch {
    // quota
  }
}

function load(): DeadLetterEntry[] {
  if (typeof window === 'undefined') return memoryDeadLetter;
  try {
    const raw = window.localStorage.getItem(DEAD_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as DeadLetterEntry[]) : [];
  } catch {
    return [];
  }
}

export function moveToDeadLetter(
  queueItemId: string,
  params: SendEventParams,
  queueType: QueueItemType,
  attempts: number,
  reason: string,
  error?: string
): boolean {
  if (!isCriticalEvent(params.type)) return false;
  const entry: DeadLetterEntry = {
    id: crypto.randomUUID(),
    queueItemId,
    type: params.type,
    queueType,
    params: { ...params },
    attempts,
    error,
    reason,
    timestamp: Date.now(),
  };
  const entries = [...load(), entry];
  memoryDeadLetter = entries;
  persist(entries);
  return true;
}

export function getDeadLetterCount(): number {
  return load().length;
}

export function getDeadLetterEntries(): DeadLetterEntry[] {
  return load();
}

export function exportDeadLetterData(): string {
  return JSON.stringify(load());
}

export function _testClearDeadLetter(): void {
  memoryDeadLetter = [];
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(DEAD_QUEUE_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}
